import { createLogger } from '@hunting-nature/shared-core/logging';
import { IMAGE_CACHE_NAME } from '../config/cache.config';

const log = createLogger('FetchCache');

/**
 * IMAGE_URL_PATTERN
 *
 * Matches any request whose URL starts with the Secacam media CDN.
 * Adjust if the domain ever changes or if additional domains serve images.
 */
const IMAGE_URL_PATTERN = /^https:\/\/media\.secacam\.com\//;

/**
 * storeResponse
 *
 * Clones the network response and stores it in the shared image cache
 * bucket with metadata headers understood by ImageCacheService.
 */
const storeResponse = async (cache: Cache, url: string, response: Response): Promise<void> => {
  try {
    const blob = await response.clone().blob();
    const now = String(Date.now());
    const headers = new Headers(response.headers);
    headers.set('x-last-read-at', now);
    headers.set('x-cached-size', String(blob.size));

    await cache.put(
      url,
      new Response(blob, {
        status: response.status,
        statusText: response.statusText,
        headers,
      })
    );
  } catch (error) {
    log.debug('Failed to store response', { error });
  }
};

/**
 * installFetchCache
 *
 * Monkey-patches window.fetch BEFORE Angular bootstraps so that every
 * fetch() call for media.secacam.com images is routed through the shared
 * CacheStorage image cache bucket.
 *
 * Images are immutable once stored: a URL's bytes never change, so no TTL
 * or background revalidation is needed.  Deletion is handled out-of-band
 * by the deletion sync mechanism.
 *
 * Strategy: cache-forever, cache-first.
 *   1. Cache hit  → return cached Response immediately, no network.
 *   2. Cache miss → fetch from network, store, return Response.
 *
 * Shares the same CacheStorage bucket as ImageCacheService so both code
 * paths are interoperable.  Eviction is handled exclusively by
 * ImageCacheService (runs on startup and after directive-driven stores).
 *
 * Call this function exactly once, at the top of main.ts, before
 * bootstrapApplication().
 */
export function installFetchCache(): void {
  if (globalThis.window === undefined || typeof globalThis.fetch !== 'function') return;
  if (typeof caches === 'undefined') return;

  const originalFetch = globalThis.fetch.bind(globalThis);

  // URLs that returned a server error (5xx), mapped to the timestamp of the
  // failure.  Within the TTL window, subsequent requests for the same URL
  // are answered with a synthetic error Response so the browser never makes
  // a real network call — which prevents repeated "Failed to load resource:
  // 500" messages in the console.  After the TTL expires we let the next
  // request through so the image loads once the server has finished
  // rendering the thumbnail.
  const serverErrorUrls = new Map<string, number>();
  const SERVER_ERROR_TTL_MS = 30_000;

  globalThis.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;

    // Only intercept image CDN requests
    if (!IMAGE_URL_PATTERN.test(url)) {
      return originalFetch(input, init);
    }

    let cache: Cache | null = null;
    try {
      cache = await caches.open(IMAGE_CACHE_NAME);
    } catch {
      // CacheStorage unavailable — pass through
      return originalFetch(input, init);
    }

    const cached = await cache.match(url);

    if (cached) {
      // Clone before consuming the body — .blob() marks the body as used,
      // which would cause .clone() to throw "Response body is already used".
      const returnResponse = cached.clone();
      // Update last-read timestamp for LRU eviction (fire-and-forget).
      void cached.blob().then(blob => {
        const headers = new Headers(cached.headers);
        headers.set('x-last-read-at', String(Date.now()));
        void cache.put(url, new Response(blob, { headers })).catch(() => {});
      });
      return returnResponse;
    }

    // Return a synthetic error for URLs that recently failed with a server
    // error — avoids a real network call and the corresponding browser
    // console noise.  Once the TTL expires we let the request through so
    // the image can load after the server has rendered the thumbnail.
    const failedAt = serverErrorUrls.get(url);
    if (failedAt !== undefined && Date.now() - failedAt < SERVER_ERROR_TTL_MS) {
      return new Response(null, { status: 500, statusText: 'Server Error (remembered)' });
    }

    // Cache miss (or TTL expired) — go to network
    let networkResponse: Response;
    try {
      networkResponse = await originalFetch(input, init);
    } catch (networkError) {
      // Network is down and nothing is cached — rethrow so callers see the real error
      throw networkError;
    }

    if (networkResponse.ok) {
      serverErrorUrls.delete(url);
      void storeResponse(cache, url, networkResponse.clone());
    } else if (networkResponse.status >= 500) {
      serverErrorUrls.set(url, Date.now());
    }

    return networkResponse;
  };

  log.debug('window.fetch patched for media.secacam.com');
}
